// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ArrowCircleDownOutlined,
  ArrowCircleRightOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  ButtonBase,
  Paper,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

interface Props {
  label?: string;
  isVisible: boolean;
  onClick: () => void;
  direction?: 'vertical' | 'horizontal';
  sx?: SxProps<Theme>;

  /**
   * The accessible name, for a control that shows only its icon. A `label`
   * names the control on its own, so supply one or the other.
   */
  ariaLabel?: string;
}

export function ScrollIndicator(props: Props) {
  const theme = useTheme();
  const isHorizontal = props.direction === 'horizontal';

  return (
    <Paper
      component={ButtonBase}
      variant="outlined"
      aria-label={props.ariaLabel}
      onClick={props.onClick}
      sx={{
        borderRadius: 'calc(infinity * 1px)',
        backgroundColor: 'white',
        position: 'absolute',
        bottom: theme.spacing(1),
        right: theme.spacing(1),
        boxShadow: theme.shadows[6],
        opacity: props.isVisible ? 1 : 0,
        pointerEvents: props.isVisible ? 'auto' : 'none',
        transition: theme.transitions.create('opacity', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.shortest,
        }),
        zIndex: 1000,
        ...props.sx,
      }}
    >
      <Stack direction="row" alignItems="center" padding={0.5} spacing={0.5}>
        {props.label && <Typography variant="body2">{props.label}</Typography>}
        {isHorizontal ? (
          <ArrowCircleRightOutlined color="action" fontSize="small" />
        ) : (
          <ArrowCircleDownOutlined color="action" fontSize="small" />
        )}
      </Stack>
    </Paper>
  );
}
